import React, { useState } from 'react';
import { FiDownload, FiChevronDown, FiCheck, FiTrendingUp, FiRefreshCw, FiLoader, FiX } from 'react-icons/fi';
import Stock from '../../../model/Stock';
import { RegisterListingResult } from '../../../model/RegisterListingResult';
import { toast } from '../Toast';
import { ReportModal, Check, FileTypeColumn, FileType } from './ReportModals';
import { reportUrl, saveCsv, asAtDate } from './reportsData';

// Share Registry pop-ups on the All holders / Top holders tabs, on the ReportModal shell from ReportModals.tsx.
// CSVs are built in the browser from the rows the page already loaded (GET registerListing / topHolders).
// The only write is the existing Refresh call, which AllHolders.tsx still owns; this file only shows its progress.

const preview = (what: string) => toast.info(`Preview only: ${what}`);

const AsAtField: React.FC<{ listing: RegisterListingResult | null }> = ({ listing }) => {
    const asAt = asAtDate(listing);
    return <label className="report-modal-field">
        <span>As at</span>
        <span className="report-modal-select">
            <select value="latest" onChange={() => undefined}>
                <option value="latest">Latest sync{asAt ? ` — ${asAt}` : ''}</option>
            </select>
            <FiChevronDown />
        </span>
    </label>;
};

const Select: React.FC<{ label: string; value: string; options: [string, string][]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) =>
    <label className="report-modal-field">
        <span>{label}</span>
        <span className="report-modal-select">
            <select value={value} onChange={e => onChange(e.target.value)}>
                {options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
            </select>
            <FiChevronDown />
        </span>
    </label>;

// ---- All holders › Download This List (Figma 211:5307, note 550:2688) ----

export interface HolderExportRow {
    rank: number | ''; name: string; holderNumber: string; holderType: string; group: string;
    location: string; holding: number; pct: number; email: string; address: string;
}

interface DownloadHoldersProps {
    stock: Stock; listing: RegisterListingResult | null;
    all: HolderExportRow[]; filtered: HolderExportRow[]; filtersOn: boolean;
    onClose: () => void;
}

// PDF and Excel have no source yet (preview only); CSV is the real listing, contact columns off by default
export const DownloadHoldersModal: React.FC<DownloadHoldersProps> = ({ stock, listing, all, filtered, filtersOn, onClose }) => {
    const [type, setType] = useState<FileType>('csv');
    const [which, setWhich] = useState(filtersOn ? 'filtered' : 'all');
    const [size, setSize] = useState(true);
    const [contacts, setContacts] = useState(false);
    const withEmail = all.filter(r => r.email);

    const options: [string, string][] = [
        ['all', `All ${all.length.toLocaleString()} holders`],
        ...(filtersOn ? [['filtered', `The ${filtered.length.toLocaleString()} matching your filters`] as [string, string]] : []),
        ['email', `Only holders with an email (${withEmail.length.toLocaleString()})`],
    ];

    const download = () => {
        if (type === 'pdf') { preview('a printable holders list isn’t built yet — pick CSV for now.'); return; }
        if (type === 'excel') { preview('Excel files aren’t built yet — the CSV has the same columns.'); return; }
        const rows = which === 'filtered' ? filtered : which === 'email' ? withEmail : all;
        if (!rows.length) { toast.error('No holders to download.'); return; }
        const head = ['#', 'Holder', 'Holder No.', 'Holder type', 'Group', 'Location',
            ...(size ? ['Holding', '% Issued'] : []), ...(contacts ? ['Email', 'Address'] : [])];
        saveCsv(`${stock.ticker}-holders.csv`, [head, ...rows.map(r => [r.rank, r.name, r.holderNumber, r.holderType, r.group, r.location,
            ...(size ? [r.holding, r.pct.toFixed(2)] : []), ...(contacts ? [r.email, r.address] : [])])]);
        onClose();
    };

    return <ReportModal className="download" icon={<FiDownload />} title="Download the Holders List"
        sub="Choose the file type — a PDF to read or print, a spreadsheet to work with"
        note="Files are made now and download straight away. Nothing is emailed." onClose={onClose}
        buttons={<button type="button" className="btn-primary" onClick={download}><FiDownload /> Download</button>}>
        <div className="report-modal-cols">
            <FileTypeColumn type={type} onChange={setType} />
            <div className="col">
                <span className="caption">What to include</span>
                <Select label="Which holders" value={which} options={options} onChange={setWhich} />
                <AsAtField listing={listing} />
                <Check on={size} onChange={setSize} title="Holding size and percentage" sub="Shares held and the percentage of the company" />
                <Check on={contacts} onChange={setContacts} title="Contact details" sub="Address and email — keep this file secure" />
            </div>
        </div>
    </ReportModal>;
};

// ---- Top holders › Download This List (Figma 549:1970, note 550:2703) ----

export interface TopExportRow { position: number; name: string; holderNumber: string; holding: number; pct: number; change: number | null }

interface DownloadTopProps { stock: Stock; listing: RegisterListingResult | null; rows: TopExportRow[]; last: number; onClose: () => void }

// PDF opens the printable Top 20 report (reportsData / ReportView); CSV is the same file the old button made, plus options
export const DownloadTopHoldersModal: React.FC<DownloadTopProps> = ({ stock, listing, rows, last, onClose }) => {
    const [type, setType] = useState<FileType>('pdf');
    const [count, setCount] = useState(String(Math.min(20, last)));
    const [change, setChange] = useState(true);
    const [contacts, setContacts] = useState(false);

    const options: [string, string][] = [...[20, 50].filter(n => n < last).map(n => [String(n), `Top ${n}`] as [string, string]),
        [String(last), `All ${last} positions`]];

    const download = () => {
        if (type === 'excel') { preview('Excel files aren’t built yet — the CSV has the same columns.'); return; }
        if (type === 'pdf') {
            if (Number(count) !== 20) { preview('the printable page covers the top 20 — pick Top 20, or CSV for more.'); return; }
            window.open(reportUrl(stock, 'top-20'), '_blank', 'noopener');
            onClose();
            return;
        }
        // contact columns come from the register listing, matched on holder number (holder groups have none)
        const holders = new Map((listing?.holders || []).map(h => [h.holderNumber, h]));
        const head = ['#', 'Holder', 'Holder No.', 'Holding', '% Issued', ...(change ? ['30-day change'] : []), ...(contacts ? ['Email', 'Address'] : [])];
        const lines = rows.filter(r => r.position <= Number(count)).map(r => [r.position, r.name, r.holderNumber, r.holding, r.pct.toFixed(2),
            ...(change ? [r.change ?? ''] : []),
            ...(contacts ? [holders.get(r.holderNumber)?.email || '', holders.get(r.holderNumber)?.address || ''] : [])]);
        saveCsv(`${stock.ticker}-top-holders.csv`, [head, ...lines]);
        onClose();
    };

    return <ReportModal className="download" icon={<FiDownload />} title="Download the Top Holders List"
        sub="Choose the file type — a PDF to read or print, a spreadsheet to work with"
        note="The PDF is the same layout as the Top 20 page of your board pack." onClose={onClose}
        buttons={<button type="button" className="btn-primary" onClick={download}><FiDownload /> Download</button>}>
        <div className="report-modal-cols">
            <FileTypeColumn type={type} onChange={setType} />
            <div className="col">
                <span className="caption">What to include</span>
                <Select label="How many holders" value={count} options={options} onChange={setCount} />
                <AsAtField listing={listing} />
                <Check on={change} onChange={setChange} title="Change since last month" sub="The 30-day change in shares held" />
                <Check on={contacts} onChange={setContacts} title="Contact details" sub="Address and email — keep this file secure" />
            </div>
        </div>
    </ReportModal>;
};

// ---- All holders › Refresh from {provider} (Figma 211:5845, note 454:5515) ----

interface RefreshProps {
    provider: string;
    lastRefresh: string | null; // registerAsAt() of the listing on screen
    holderCount: number | null;
    refreshing: boolean;
    started: boolean; // a refresh ran since the modal opened (shows the result instead of the confirm step)
    error: string;
    onRefresh: () => void; // AllHolders.tsx's existing refreshRegisterListing call
    onClose: () => void;
}

// Confirm (Refresh now · Cancel), then progress. The refresh is a single request, so the steps only show
// "asking" / "done" — no per-stage progress or time left exists to show.
export const RefreshRegisterModal: React.FC<RefreshProps> = ({ provider, lastRefresh, holderCount, refreshing, started, error, onRefresh, onClose }) => {
    const last = `Last successful refresh: ${lastRefresh || '—'}`;

    if (!refreshing && !started) return <ReportModal className="holders-refresh" icon={<FiTrendingUp />} title={`Refresh from ${provider}`}
        sub="Asks the registry for fresh data now instead of waiting for the nightly sync" note={last} onClose={onClose}
        buttons={<button type="button" className="btn-primary" onClick={onRefresh}><FiRefreshCw /> Refresh now</button>}>
        <p className="holders-refresh-text">This can take a minute. You can keep working while it runs — we’ll show a notification when it’s done.</p>
    </ReportModal>;

    const failed = !refreshing && !!error;
    const step = (state: 'done' | 'active' | 'pending' | 'failed', text: string) =>
        <div className={`holders-refresh-step ${state}`}>
            <span className="dot">{state === 'done' ? <FiCheck /> : state === 'active' ? <FiLoader className="spin" /> : state === 'failed' ? <FiX /> : null}</span>
            {text}
        </div>;

    return <ReportModal className="holders-refresh" icon={<FiTrendingUp />} hideCancel onClose={onClose}
        title={refreshing ? 'Refreshing the Register…' : failed ? 'Refresh Didn’t Finish' : 'Register Refreshed'}
        sub={refreshing ? `Pulling today’s data from ${provider}. You can keep working.` : failed ? error : `Today’s data from ${provider} is on the page.`}
        note={refreshing ? 'We’ll show a notification when it’s done' : ''}
        buttons={refreshing
            ? <button type="button" className="btn-outline holders-refresh-bg" onClick={onClose}>Run in Background</button>
            : <button type="button" className="btn-primary" onClick={onClose}>Done</button>}>
        <div className="holders-refresh-steps">
            {step(refreshing ? 'active' : failed ? 'failed' : 'done', `Asking ${provider} for the latest register`)}
            {step(refreshing ? 'pending' : failed ? 'pending' : 'done',
                `Downloading holder records${!refreshing && !failed && holderCount !== null ? ` (${holderCount.toLocaleString()})` : ''}`)}
        </div>
        <div className={`holders-refresh-bar${refreshing ? ' running' : failed ? ' failed' : ''}`}><span /></div>
        <span className="holders-refresh-meta">{refreshing ? `Usually under a minute · ${last}` : last}</span>
    </ReportModal>;
};
